import { GROUP_BY_COLUMN_DATA } from '../../shared/constants';
import { alignRightCellStyle, alignCenterCellStyle } from '@common/cellStyles';
import { legalEntityColumnDefinition, businessUnitColumnDefinition } from '../../../shared/columnDefinitions';
import { CellExportFormats } from '@common/xlsxExport/xlsxExport.constants';
import TaxExpenseTooltipRenderer from '../drillDetailLinkCellRenderer.component';
import { numeralValueFormatter } from '../../../shared/numeralValueFormatter';
import { NumeralTypes } from '@common/utils/formatNumberValue';
import { customNumeralValueFormatter } from '../../../shared/customNumeralValueFormatter';

export default ({
  groupByName,
  groupByField,
  hasSourceCurrency,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
}) => [
  {
    headerName: groupByName,
    field: groupByField,
    minWidth: 120,
    pinned: 'left',
    sort: 'asc',
    ...alignCenterCellStyle,
  },
  ...(groupByName === GROUP_BY_COLUMN_DATA.CC.groupByName)
    ? [
      { ...legalEntityColumnDefinition, headerName: 'Legal Entity', field: 'LEGAL_ENTITY' },
      { ...businessUnitColumnDefinition, field: 'BUSINESS_UNIT' },
    ] : [],
  {
    headerName: 'Currency',
    field: 'FUNC_CURRENCY',
    minWidth: 120,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'GAAP Income/(Loss) Before Tax',
    field: 'GAAP_IBT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'G2S Income/(Loss) Before Tax',
    field: 'G2S_ADJ',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Statutory Income/(Loss) Before Tax',
    field: 'STAT_IBT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Permanent Differences',
    field: 'B2T_PERM',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    cellRendererParams: {
      useHistorical: true,
    },
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Temporary Differences',
    field: 'B2T_TEMP',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    cellRendererParams: {
      useHistorical: true,
    },
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Taxable Income/(Loss)',
    field: 'TXBL_INC',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Rate',
    field: 'TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'STAT Current Tax Expense/(Benefit) Before Credits',
    field: 'GAAP_CUR_TAX_B4_CR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Credits and Other After Tax Adjustments',
    field: 'TOTAL_CR',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    cellRendererParams: {
      useHistorical: true,
    },
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'STAT Current Tax Expense',
    field: 'GAAP_CUR_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'S2T Deferred Tax Expense',
    field: 'GAAP_DEF_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'G2S Deferred Tax Expense (P)',
    field: 'G2S_DEF_TAX_P',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'G2S Deferred Tax Expense (G)',
    field: 'GAAP_G2S_DEF_TAX_G',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: hasSourceCurrency
      ? 'Tax Expense on CY Activity (STAT Functional Currency)'
      : 'Tax Expense on CY Activity',
    field: 'TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'CY Effective Tax Rate',
    field: 'CY_EFF_TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Currency',
    field: 'TAX_CURRENCY',
    minWidth: 120,
    hide: !hasSourceCurrency,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'MOR',
    field: 'TAX_CUR_MOR',
    minWidth: 120,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Expense on CY Activity',
    field: 'CUR_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Expense on CY Activity',
    field: 'DEF_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Expense on CY Activity (Tax Currency)',
    field: 'TAX_EXP_CY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    hide: !hasSourceCurrency,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Current)',
    field: 'RTA_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Deferred)',
    field: 'RTA_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Post Return Adjustments (Current)',
    field: 'PRA_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Post Return Adjustments (Deferred)',
    field: 'PRA_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'True Up (Payable)',
    field: 'TRUE_UP_PAY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'True Up (Deferred)',
    field: 'TRUE_UP_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Rate Changes (Deferred)',
    field: 'RATE_CHG_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Current)',
    field: 'RES_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Non Current)',
    field: 'RES_NON_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Penalty)',
    field: 'RES_PEN',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Provision (Deferred)',
    field: 'OTH_PROV_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: !hasSourceCurrency
      ? 'Tax Expense on Prior Year Activity'
      : 'Tax Expense on Prior Year Activity (Tax Currency)',
    field: 'TAX_EXP_PY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Tax Expense',
    field: 'TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: hasSourceCurrency
      ? 'STAT Income/(Loss) Before Tax (Tax Currency)'
      : 'Income/(Loss) Before Tax',
    field: 'STAT_IBT_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'STAT Effective Tax Rate',
    field: 'STAT_ETR',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
];
